// Shell-safe article identities: slug, title, description and the two public
// dates. No body text lives here; article bodies load with the page chunk.
import type { LearnSlug } from './publication';

export interface LearnCatalogEntry {
  slug: LearnSlug;
  /** At most 60 characters and ends with " | Lendwise"; the headline is the part before the suffix. */
  title: string;
  /** 70 to 160 characters, written for the search result, not the page. */
  description: string;
  topic: 'dscr';
  /** Set once by `learn:stamp --publish` and never moved. Absent until the article publishes. */
  publishedOn?: string;
  /** Moves only with a substantive edit (`learn:stamp --substantive`); feeds the sitemap lastmod. */
  updatedOn?: string;
}

const BRAND_SUFFIX = ' | Lendwise';

export const LEARN_CATALOG: readonly LearnCatalogEntry[] = [
  {
    slug: 'do-dscr-loans-require-20-percent-down',
    title: 'Do DSCR loans require 20% down? | Lendwise',
    description: 'What a DSCR loan usually asks for as a down payment, when 15% down can work, and how your credit, the rent ratio and the property type move the number.',
    topic: 'dscr',
    publishedOn: '2026-11-02',
    updatedOn: '2026-11-02',
  },
  {
    slug: 'dscr-loan-requirements',
    title: 'DSCR loan requirements, in plain English | Lendwise',
    description: 'The credit score, reserves, rent coverage and paperwork a DSCR loan asks for, and the honest list of what it does not need from you.',
    topic: 'dscr',
    publishedOn: '2026-11-02',
    updatedOn: '2026-11-02',
  },
  {
    slug: 'what-is-a-good-dscr',
    title: 'What is a good DSCR? | Lendwise',
    description: 'How the debt service coverage ratio is calculated, what 1.00 and 1.25 mean for a rental, how far below 1.00 some programs go, with a calculator you can use.',
    topic: 'dscr',
    publishedOn: '2026-11-02',
    updatedOn: '2026-11-02',
  },
  {
    slug: 'dscr-loan-downsides',
    title: 'What is the downside of a DSCR loan? | Lendwise',
    description: 'The real trade-offs of a DSCR loan: cost, prepayment penalties, the down payment and the rent test, and the situations where it is still the better choice.',
    topic: 'dscr',
    publishedOn: '2026-11-02',
    updatedOn: '2026-11-02',
  },
  {
    slug: 'dscr-vs-conventional-loan',
    title: 'DSCR loan vs. conventional loan | Lendwise',
    description: 'How a DSCR loan and a conventional investment property loan differ in paperwork, qualifying math, ownership, loan count and who each one tends to fit.',
    topic: 'dscr',
    publishedOn: '2026-11-02',
    updatedOn: '2026-11-02',
  },
  {
    slug: 'dscr-loan-llc',
    title: 'Can an LLC get a DSCR loan? | Lendwise',
    description: 'How DSCR loans work when the rental is held in an LLC: who signs, the personal guaranty, the documents lenders ask for, and what to check before you apply.',
    topic: 'dscr',
    publishedOn: '2026-11-02',
    updatedOn: '2026-11-02',
  },
  {
    slug: 'how-many-dscr-loans-can-you-have',
    title: 'How many DSCR loans can you have? | Lendwise',
    description: 'Why there is no single limit on DSCR loans, how lender caps and reserves work across several rentals, and the loan terms investors usually choose.',
    topic: 'dscr',
    publishedOn: '2026-11-02',
    updatedOn: '2026-11-02',
  },
  {
    slug: 'dscr-loan-short-term-rental',
    title: 'DSCR loans for short-term rentals | Lendwise',
    description: 'When a DSCR loan can finance a short-term rental, how the rent is counted, the conditions that come with it, and why personal use is off the table.',
    topic: 'dscr',
    publishedOn: '2026-11-02',
    updatedOn: '2026-11-02',
  },
  {
    slug: 'dscr-loan-refinance-cash-out',
    title: 'DSCR refinance and cash-out, explained | Lendwise',
    description: 'How a DSCR refinance works: the equity you keep, seasoning, rent documentation, what the cash can be used for, and the business-purpose test.',
    topic: 'dscr',
    publishedOn: '2026-11-02',
    updatedOn: '2026-11-02',
  },
  {
    slug: 'who-offers-dscr-loans',
    title: 'Who offers DSCR loans? | Lendwise',
    description: 'Why most banks do not offer DSCR loans, what kind of lender does, and a short checklist for finding out whether yours will.',
    topic: 'dscr',
    publishedOn: '2026-11-02',
    updatedOn: '2026-11-02',
  },
];

export function learnHeadline(entry: LearnCatalogEntry): string {
  return entry.title.endsWith(BRAND_SUFFIX) ? entry.title.slice(0, -BRAND_SUFFIX.length) : entry.title;
}

export function catalogEntry(slug: LearnSlug): LearnCatalogEntry {
  const entry = LEARN_CATALOG.find(candidate => candidate.slug === slug);
  if (!entry) throw new Error(`The Learn catalog has no entry for ${slug}`);
  return entry;
}
